<template>
  <Dialog
    v-if="!isDataEmpty"
    v-model="dialog"
    show-close
    :show-back-arrow="false"
    :title="
      isUpdate
        ? $t(`medicalProcedure.${medicalProcedureData.type}.type`)
        : $t(`medicalProcedure.${medicalProcedureData.type}.new`)
    "
    :left-header-icon="
      $vuetify.breakpoint.smAndDown ? null : `icons/${medicalProcedureData.icon}.svg`
    "
    :header-type="$vuetify.breakpoint.smAndDown ? 'navigation' : 'information'"
    :show-title-in-content="false"
    :buttons="dialogButtons"
    :show-title-before-scroll="false"
  >
    <template #text>
      <div v-if="$vuetify.breakpoint.smAndDown">
        <v-img
          width="56"
          contain
          class="mb-4"
          :src="$asset(`img/icons/${medicalProcedureData.icon}.svg`)"
        />
      </div>
      <div class="grey-darken-1--text mt-0 mt-md-6">
        {{ medicalProcedureData.title }}
      </div>
      <template v-if="!isUpdate">
        <div class="text-h2 mb-4">
          {{ $t(`medicalProcedure.${medicalProcedureData.type}.new`) }}
        </div>
        {{ $t(`medicalProcedure.${medicalProcedureData.type}.newFormPerex`) }}<br />
      </template>
      <template v-else>
        <div class="text-h2 mb-4">
          {{ $t(`medicalProcedure.${medicalProcedureData.type}.type`) }}
        </div>
        {{ $t(`medicalProcedure.${medicalProcedureData.type}.editFormPerex`) }}<br />
      </template>
      <MedicalProcedureForm
        :medical-procedure-data="medicalProcedureData"
        class="mt-4"
        :is-update="isUpdate"
        @close="dialog = false"
      />
    </template>
  </Dialog>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import preventionData from '@/mixins/preventionData';
import lodash from 'lodash';
import Dialog from '@/components/Atoms/Dialog';
import MedicalProcedureForm from '@/components/Forms/MedicalProcedureForm';

export default {
  name: 'MedicalProcedureDialog',
  components: { MedicalProcedureForm, Dialog },
  mixins: [preventionData],
  computed: {
    dialog: {
      get() {
        return this.$store.state.profile.dashboard.dialog === 'medicalProcedure';
      },
      set(value) {
        this.$store.commit('profile/dashboard/setDialog', value);
      }
    },
    ...mapGetters({
      editEntryFormData: 'profile/prevention/editEntryFormData',
      medicalProcedureData: 'profile/prevention/editEntryMedicalProcedureData',
      dialogButtons: 'profile/medicalProcedure/dialogButtons'
    }),
    isDataEmpty() {
      return lodash.isEmpty(this.medicalProcedureData);
    },
    isUpdate() {
      return !lodash.isEmpty(this.editEntryFormData);
    }
  },
  methods: {
    ...mapMutations({
      updateDate: 'forms/date/updateDate',
      updateTime: 'forms/time/updateTime',
      updateEntryNotes: 'profile/prevention/updateEntryNotes',
      resetAttachments: 'forms/attachments/resetAttachments',
      setEditEntryFormData: 'profile/prevention/setEditEntryFormData',
      setEditEntryMedicalProcedureData: 'profile/prevention/setEditEntryMedicalProcedureData',
      setAttachments: 'forms/attachments/setAttachments'
    }),
    async closeDialog() {
      let medicalProcedureId = this.medicalProcedureData.id;

      // reset medical procedure form data
      this.setEditEntryFormData({});
      this.setEditEntryMedicalProcedureData({});
      this.$eventHub.$emit('resetDateSegmentsValidation');
      this.updateDate('');
      this.updateTime('');
      this.updateEntryNotes('');
      this.resetAttachments();

      // reload medical procedures to apply the changes in reports/appointments in whole prevention
      await this.setExaminations([]);
      await this.setVaccinations([]);
      await this.getPreventionData();
      if (medicalProcedureId) {
        await this.loadReports(medicalProcedureId);
        await this.loadAppointments(medicalProcedureId);
      }
    }
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        if (lodash.isEmpty(this.medicalProcedureData)) {
          this.dialog = false;
        } else {
          if (!lodash.isEmpty(this.editEntryFormData)) {
            const entryDateObject = new Date(
              this.editEntryFormData.dateTime || this.editEntryFormData.date
            );
            const entryDateString =
              `${entryDateObject.getFullYear()}-` +
              `${this.padStartWithZeroes(entryDateObject.getMonth() + 1, 2)}-` +
              `${this.padStartWithZeroes(entryDateObject.getDate(), 2)}`;
            const entryTimeString =
              `${this.padStartWithZeroes(entryDateObject.getHours(), 2)}:` +
              `${this.padStartWithZeroes(entryDateObject.getMinutes(), 2)}`;

            this.updateEntryNotes(this.editEntryFormData.text);
            this.setAttachments(this.editEntryFormData.attachments);
            this.updateDate(entryDateString);
            this.updateTime(entryTimeString);
          } else {
            this.updateEntryNotes('');
            this.updateDate('');
            this.updateTime('');
            this.setAttachments([]);
          }
          this.gtmPush({
            event: 'form_start',
            'form.name': lodash.isEmpty(this.editEntryFormData)
              ? 'zapsani report'
              : 'editace report',
            'medicalProcedure.type': this.medicalProcedureData.type,
            'medicalProcedure.name': this.medicalProcedureData.title
          });
          this.gtmPush({
            event: 'form_started',
            'form.name': lodash.isEmpty(this.editEntryFormData)
              ? 'zapsani report'
              : 'editace report',
            'medicalProcedure.type': this.medicalProcedureData.type,
            'medicalProcedure.name': this.medicalProcedureData.title
          });
        }
      } else {
        this.$nextTick(this.closeDialog);
      }
    }
  }
};
</script>
